import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Video, Calendar, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

export default function SessionsPage() {
  const [types, setTypes] = useState({});
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "therapy_1on1", scheduled_at: "", topic: "", notes: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    const [t, s] = await Promise.all([api.get("/sessions/types"), api.get("/sessions/mine")]);
    setTypes(t.data);
    setSessions(s.data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  async function book(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sessions/book", form);
      setForm({ type: "therapy_1on1", scheduled_at: "", topic: "", notes: "" });
      setOpen(false);
      toast.success("Session requested");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  return (
    <div data-testid="sessions-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Locked In Live</p>
          <h1 className="font-serif font-light text-4xl">Couples therapy & live sessions</h1>
          <p className="text-sm text-[var(--muted)] mt-2">Book a 1:1 with Mr. & Mrs. Don, premarital counseling, or join a live broadcast.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="book-session-btn" className="btn-gold"><Plus className="w-4 h-4" /> Book a Session</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {Object.entries(types).map(([key, info]) => (
          <button key={key} onClick={() => { setForm({ ...form, type: key }); setOpen(true); }} className="bg-white border border-[var(--line)] rounded-2xl p-5 text-left hover:border-[var(--gold)] transition-colors">
            <Video className="w-5 h-5 text-[var(--gold)] mb-3" />
            <p className="font-serif text-lg leading-tight mb-1">{info.label}</p>
            <p className="text-xs text-[var(--muted)]">{info.duration_min} min</p>
          </button>
        ))}
      </div>

      <div className="bg-white border border-[var(--line)] rounded-2xl">
        <div className="px-5 py-4 border-b border-[var(--line)]"><p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">Your Sessions</p></div>
        {sessions.length === 0 && <p className="px-5 py-12 text-center text-[var(--muted)]"><Calendar className="w-8 h-8 mx-auto mb-3 text-[var(--gold)]" />No sessions yet. Book your first.</p>}
        {sessions.map((s) => (
          <div key={s.session_id} className="px-5 py-4 border-t border-[var(--line)] first:border-t-0 flex flex-col md:flex-row gap-3 md:items-center">
            <div className="flex-1">
              <p className="font-serif text-lg">{(types[s.type] || {}).label || s.type}</p>
              <p className="text-xs text-[var(--muted)] mt-1">{new Date(s.scheduled_at).toLocaleString()} {s.topic && `· ${s.topic}`}</p>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full ${s.status === "confirmed" ? "bg-[#e9f3e7] text-[#2f6f2f]" : s.status === "completed" ? "bg-[var(--bg-warm)]" : "bg-[#fff2d4] text-[#7a5b13]"}`}>{s.status}</span>
            <Link to={`/session/${s.session_id}`} data-testid={`join-${s.session_id}`} className="btn-outline-gold text-xs py-2 px-4">Join Room <ExternalLink className="w-3 h-3" /></Link>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md">
            <div className="flex justify-between mb-6"><h3 className="font-serif text-2xl">Book Session</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <form onSubmit={book} className="space-y-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Session Type</label>
                <select value={form.type} onChange={(e) => setForm({...form, type: e.target.value})} className="input-luxe">
                  {Object.entries(types).map(([k, info]) => <option key={k} value={k}>{info.label}</option>)}
                </select>
              </div>
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Date & Time</label><input data-testid="session-when" required type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({...form, scheduled_at: e.target.value})} className="input-luxe" /></div>
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Topic</label><input value={form.topic} onChange={(e) => setForm({...form, topic: e.target.value})} className="input-luxe" placeholder="What would you like to focus on?" /></div>
              <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Notes (optional)</label><textarea rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} className="input-luxe resize-none" /></div>
              <button type="submit" disabled={busy} data-testid="session-submit" className="btn-gold w-full">{busy ? "Booking…" : "Request Session"}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
